import React, { useEffect, useState } from "react";
import { Alert, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import { ButtonLoading } from "go-form";
import handleError from "go-app/services/errors";
import handleException from "go-core/api/handleException";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { NEW_WAY_TO_ENCODING_FILTER_SIGN } from "go-list/core/components/Filter/services";
import useFlash from "../../../../../../../../lib/go-alert/AlertMessage";
import { partnerApi } from "../../../../../../../../services/Api/Partner/partnerApi";
import { LoanApi, OrganizationApi } from "../../../../../../../../services/Api/types";
import { api, restApi } from "../../../../../../../../services/Security/api";
import { selectUser } from "../../../../../../../../services/users/selectors";
import LoanMissingData from "../../../../../../components/Loan/LoanMissingData";
import LoanPreview from "../../../../../../components/Loan/LoanPreview/LoanPreview";
import NewLoanPreview from "../../../../../../components/Loan/NewLoanPreview/NewLoanPreview";
import NoLoanAccess from "../../components/NoLoanAccess";
import NoOrganizationLoanAccess from "../../components/NoOrganizationLoanAccess";
import { getParsedLoanItems } from "../../utils";

const PartnerLoansExternalNew2Page = () => {
	const urlSearchParams = new URLSearchParams(window.location.search);
	const organizationId = urlSearchParams.get("organization_id");
	const redirect_uri = urlSearchParams.get("return_url");
	const refId = urlSearchParams.get("reference_id") ?? "";
	const devices = urlSearchParams.getAll("devices");
	const userRoles = useSelector(selectUser)?.roles;
	const isAdminOrOffice = userRoles?.includes("ADMIN") || userRoles?.includes("OFFICE");
	const { addFlash, addSuccessFlash } = useFlash();
	const [partners, setPartners] = useState<undefined | Record<string, any>[]>(undefined);
	const [loan, setLoan] = useState<undefined | LoanApi>(undefined);
	const [organization, setOrganization] = useState<OrganizationApi | null | undefined>(undefined);
	const [noAccess, setNoAccess] = useState(false);
	const { t } = useTranslation("admin");
	const history = useHistory();
	const [loading, setLoading] = useState(false);
	const items = getParsedLoanItems(devices);
	const isLoanFulfilled = !!(refId && organizationId && items && items.length > 0);
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		init();
	}, []);

	useEffect(() => {
		handleFetchLoan();
	}, [organization]);

	const init = async () => {
		handleChangeTabTitle(`${t("modules.loan.header.title")} | ${t("modules.partner.field.partner_panel.title")}`);
		await checkOrganization();
	};

	const checkOrganization = async () => {
		try {
			const org = await partnerApi.checkOrganizationForLoan(Number(organizationId));
			setOrganization(org);
		} catch (e) {
			const errors = handleException(e);
			if (errors[0].code === "organization_not_found" || errors[0].code === "access_forbidden") {
				setOrganization(null);
			} else {
				handleError.alert(e, addFlash);
			}
		}
	};

	const checkIfLoanHasSameParameters = (loan: LoanApi) => {
		const loanDevices = loan?.items?.map((item) => `${item.name}|${item.serial_id}`);
		if (
			JSON.stringify(loanDevices) === JSON.stringify(devices) &&
			Number(organizationId) === loan.organization.id &&
			refId === loan.reference_id &&
			loan.status === "DRAFT"
		)
			history.push(`/partnership/${loan.partner?.id}/loans/${loan.id}`);
	};

	const fetchLoanPartners = async () => {
		try {
			const res = await restApi.get(
				`partners/search_select?f=${btoa(
					unescape(encodeURIComponent(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}parent_slug=posnet`))
				)}`
			);
			return res.data.data;
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleCreatePartnerLoan = async () => {
		const partnersRes = await fetchLoanPartners();
		const loanPartner =
			partnersRes.length === 1
				? {
						id: partnersRes[0].id,
						name: partnersRes[0].name,
				  }
				: undefined;
		setPartners(
			partnersRes.map((x: { id: number | string; label: string; email: string }) => {
				return {
					id: Number(x.id),
					name: x.label,
					email: x.email,
				};
			})
		);
		createLoan(organization!, loanPartner);
	};

	const handleFetchLoan = async () => {
		if (!organization || !refId || !organizationId) return;

		try {
			const res = await api.user().findOrganizationLoanByReferenceId(refId);
			if (res) {
				checkIfLoanHasSameParameters(res);
			}
			if (res && Object.keys(res).length > 0) {
				if (res.status === "DRAFT") {
					setLoan({
						...res,
						organization_id: Number(organizationId),
						organization,
						items: getParsedLoanItems(devices),
					});
				} else {
					history.push(`/partnership/${res.partner?.id}/loans/${res.id}`);
				}
			} else if (isAdminOrOffice) {
				createLoan(organization, undefined);
			} else {
				await handleCreatePartnerLoan();
			}
		} catch (err) {
			const error = handleException(err);
			if (error && error[0].code === "access_forbidden") {
				setNoAccess(true);
			}
		}
	};

	const createLoan = (org: OrganizationApi, partner: Record<string, any> | undefined) => {
		if (!organizationId) return;

		setLoan({
			reference_id: refId,
			organization_id: Number(organizationId),
			organization: org,
			items: devices.length ? getParsedLoanItems(devices) : [],
			partner: partner
				? {
						id: partner.id,
						name: partner.name,
				  }
				: undefined,
			partner_id: partner ? partner.id : undefined,
		} as LoanApi);
	};

	const overrideLoan = async () => {
		if (!loan?.partner?.id) return;

		setLoading(true);
		try {
			const res = await partnerApi.updateLoan(loan.partner.id, loan);
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
			history.push(`/partnership/${loan.partner?.id}/loans/${res.id}`);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	if (noAccess) {
		return <NoLoanAccess refId={refId} />;
	}

	if (!isLoanFulfilled) {
		return (
			<LoanMissingData
				redirectUri={urlSearchParams.get("return_url")}
				loan={{ ...loan, type: "POSNET" } as LoanApi}
			/>
		);
	}

	if (organization && loan) {
		return (
			<div>
				<div className={"content-header"}>
					<h1>{t("modules.loan.header.title")}</h1>
				</div>
				<div className={"content-preview"}>
					{
						<>
							{loan.id ? (
								<>
									{loan.status === "DRAFT" && (
										<div>
											<Alert variant={"warning"}>
												<Link to={`/partnership/${loan.partner?.id}/loans/${loan.id}`}>
													{t("modules.loan.field.already_exists.title")}
												</Link>
											</Alert>
											<LoanPreview loan={loan} handleUpdateLoan={setLoan} />
											<Form.Group className="form-group">
												<ButtonLoading loading={loading} onClick={overrideLoan}>
													{t("modules.loan.action.override_loan.title")}
												</ButtonLoading>
											</Form.Group>
										</div>
									)}
								</>
							) : (
								<NewLoanPreview
									externalPartners={partners}
									adminOrOffice={isAdminOrOffice}
									loan={{
										...loan,
										type: "POSNET",
									}}
									organization={organization}
								/>
							)}
						</>
					}
				</div>
			</div>
		);
	}

	if (organization === null) {
		return <NoOrganizationLoanAccess redirect_uri={redirect_uri} />;
	}

	return <LoadingContainer />;
};
export default PartnerLoansExternalNew2Page;
